/**
 * Voice input: the client posts one recording to its server, which transcribes
 * it through the inference proxy and answers with the text.
 *
 * The request body is the raw recording, with `Content-Type` set to the
 * recording's `audio/*` MIME type and `Content-Length` set; the server token
 * authenticates it like every other server HTTP route.
 */
import * as Schema from "effect/Schema";
import { InferenceFailureKind } from "./providerRuntime.js";
/** Under `/api` so the dev client's proxy forwards it to the server. */
export const TRANSCRIPTIONS_ROUTE_PATH = "/api/transcriptions";
/** The upstream transcription API's own upload limit. */
export const VOICE_TRANSCRIPTION_MAX_BYTES = 25 * 1024 * 1024;
const BASE_TIMEOUT_MS = 120_000;
const UPLOAD_TIMEOUT_MS_PER_MEGABYTE = 10_000;
/**
 * How long the server waits for the upstream transcription of a recording of
 * this size. A long recording takes proportionally longer to upload and to
 * transcribe, so one fixed timeout either cuts long recordings off or leaves a
 * short one hanging. A client waiting on the server allows at least this much
 * plus its own upload time, or it abandons a transcription the server still
 * completes and bills.
 */
export function voiceTranscriptionTimeoutMs(sizeBytes) {
    return BASE_TIMEOUT_MS + Math.ceil(sizeBytes / (1024 * 1024)) * UPLOAD_TIMEOUT_MS_PER_MEGABYTE;
}
export const TranscriptionResponse = Schema.Struct({
    text: Schema.String,
});
/**
 * Why a transcription produced no text. The client localizes each reason; the
 * server sends no prose.
 *
 * - `voice_input_disabled` (403): the plan in force for this account turns
 *   `features.voiceInput` off.
 * - `not_configured` (503): the backend has no transcription provider set up.
 *   An unconfigured capability, shown as «не подключён»; retrying cannot help.
 * - `too_large` (413): the recording exceeds {@link VOICE_TRANSCRIPTION_MAX_BYTES}.
 * - `invalid_recording` (400): the body is not an `audio/*` recording, is
 *   empty, or is shorter than its declared length.
 * - `upload_stalled` (408): the recording stopped arriving mid-upload.
 * - `unavailable` (502, or 500 on an unexpected server fault): a configured
 *   service failed. With a `kind`, the proxy refused the request and the client
 *   shows the wording it shows for a turn that failed the same way. Without
 *   one — network, timeout, no inference credential held, or a status that
 *   names no kind — it is an outage, shown as «временно недоступен».
 */
export const TranscriptionFailureReason = Schema.Literals([
    "voice_input_disabled",
    "not_configured",
    "too_large",
    "invalid_recording",
    "upload_stalled",
    "unavailable",
]);
/** Body of every non-2xx answer the route itself produces. Auth refusals keep their own shape. */
export const TranscriptionErrorResponse = Schema.Struct({
    error: TranscriptionFailureReason,
    /** Present only with `unavailable`, when the proxy's answer classifies as a failed turn would. */
    kind: Schema.optionalKey(InferenceFailureKind),
});
